import type { IHelpdeskConfig, IHelpdeskTicketUpdatedEvent } from "@kuika/kuika-cl-model";
import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamHttpError, StreamingHelperService } from "../../../services/streaming-helper-service";
import { loadHelpdeskConfig } from "./helpdesk-config";

export type HelpdeskEventListener = (event: IHelpdeskTicketUpdatedEvent) => void;

/** How the stream is opened; injectable so the connection logic is testable without a server. */
export interface IHelpdeskEventSource {
  isEnabled: () => Promise<boolean>;
  stream: (onEvent: (data: unknown) => void, signal: AbortSignal) => Promise<void>;
  wait: (ms: number) => Promise<void>;
}

const RECONNECT_DELAY_MS: number = 3000;
const NOT_FOUND: number = 404;

export const DEFAULT_HELPDESK_EVENT_SOURCE: IHelpdeskEventSource = {
  isEnabled: async (): Promise<boolean> => {
    const config: IHelpdeskConfig = await loadHelpdeskConfig();
    return config.provider === "Itsm";
  },
  stream: (onEvent: (data: unknown) => void, signal: AbortSignal): Promise<void> =>
    StreamingHelperService.streamEvents(
      `${CommonHelperFunctions.GetBackendUrl()}/helpdesk/events/stream`,
      onEvent,
      signal
    ),
  wait: (ms: number): Promise<void> =>
    new Promise<void>((resolve: () => void): void => {
      setTimeout(resolve, ms);
    })
};

export function isHelpdeskTicketUpdatedEvent(data: unknown): data is IHelpdeskTicketUpdatedEvent {
  const event: IHelpdeskTicketUpdatedEvent | null = data as IHelpdeskTicketUpdatedEvent | null;
  return event?.type === "helpdesk.ticket_updated" && typeof event.ticketNumber === "number";
}

/**
 * The signed-in user's helpdesk updates, pushed by CoreApi when the Kuika ITSM reports a change to
 * one of their tickets. One fetch-based SSE connection is shared by every listener (the badge and
 * an open helpdesk): it opens with the first listener, closes with the last, and reconnects after a
 * drop. It is never opened for the Zendesk helpdesk, and stops for good if CoreApi answers 404
 * (the channel is off there).
 */
export class HelpdeskEventHub {
  private readonly listeners: Set<HelpdeskEventListener> = new Set<HelpdeskEventListener>();
  private running: boolean = false;
  // Each start gets its own generation, so a loop left over from an earlier start (still waiting
  // on the config, say) can tell it has been superseded and never runs alongside the new one.
  private generation: number = 0;
  private abort: AbortController | null = null;

  constructor(private readonly source: IHelpdeskEventSource) {}

  subscribe(listener: HelpdeskEventListener): () => void {
    this.listeners.add(listener);
    if (!this.running) {
      this.running = true;
      this.generation += 1;
      void this.run(this.generation);
    }
    return (): void => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) this.stop();
    };
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  private stop(): void {
    this.running = false;
    this.abort?.abort();
    this.abort = null;
  }

  private dispatch = (data: unknown): void => {
    if (!isHelpdeskTicketUpdatedEvent(data)) return;
    for (const listener of [...this.listeners]) {
      try {
        listener(data);
      } catch {
        // One failing listener must not starve the others.
      }
    }
  };

  private async run(generation: number): Promise<void> {
    const isCurrent = (): boolean => this.running && this.generation === generation;
    let enabled: boolean = false;
    try {
      enabled = await this.source.isEnabled();
    } catch {
      enabled = false;
    }
    if (!isCurrent()) return;
    if (!enabled) {
      this.running = false;
      return;
    }

    while (isCurrent()) {
      const controller: AbortController = new AbortController();
      this.abort = controller;
      try {
        await this.source.stream(this.dispatch, controller.signal);
      } catch (error) {
        if (error instanceof StreamHttpError && error.status === NOT_FOUND) {
          if (isCurrent()) this.running = false;
          break;
        }
        // A dropped connection or network error: reconnect after a pause.
      }
      if (!isCurrent()) break;
      await this.source.wait(RECONNECT_DELAY_MS);
    }
  }
}

const sharedHub: HelpdeskEventHub = new HelpdeskEventHub(DEFAULT_HELPDESK_EVENT_SOURCE);

/** Listen for the user's helpdesk updates; returns the unsubscribe function. */
export function subscribeHelpdeskEvents(listener: HelpdeskEventListener): () => void {
  if (typeof fetch === "undefined") return (): void => {};
  return sharedHub.subscribe(listener);
}
